import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";
import Swal from "sweetalert2";
import {
  MDBBadge,
  MDBBtn,
  MDBBtnGroup,
  MDBCard,
  MDBCardBody,
  MDBIcon,
  MDBTable,
} from "mdbreact";
import { GenericProducts } from "../../../../../constants";
import { axioKit } from "../../../../../services/utilities";
import TableRowCount from "../../../../../components/pagination/rows";
import Pagination from "../../../../../components/pagination";
import { CONSUMABLE_PHASES } from "../../../manager/commerce/merchandise/productGenerics/phases";
import ProductForm from "./form";
import ProductView from "./view";
import SupplierCell from "./supplierCell";
import {
  url,
  SUPPLIERS_URL,
  packLabel,
  supplierIdsOf,
  supplierLabel,
  variantsOf,
} from "./common";

const FILTERS = [
  { key: "", label: "All" },
  { key: "registered", label: "With brand" },
  { key: "none", label: "No brand" },
];

// × inside a search input that clears what was typed
const ClearButton = ({ show, onClick }) =>
  show ? (
    <button
      type="button"
      className="btn btn-link position-absolute p-0 m-0 text-muted"
      style={{
        top: "50%",
        right: 10,
        transform: "translateY(-50%)",
        boxShadow: "none",
        lineHeight: 1,
      }}
      title="Clear"
      aria-label="Clear search"
      onClick={onClick}
    >
      <MDBIcon icon="times" />
    </button>
  ) : null;

const includes = (values, key) =>
  values.some((value) => (value || "").toLowerCase().includes(key));

// default variant of the generic (one per generic): filled star; others can be set
const DefaultStar = ({ isDefault, onClick }) => (
  <MDBIcon
    icon="star"
    far={!isDefault}
    className={`mr-1 ${isDefault ? "text-warning" : "text-muted"}`}
    style={{ cursor: isDefault ? "default" : "pointer" }}
    title={
      isDefault
        ? "Default of the generic: pre-selected when tagging and dispensing"
        : "Set as default of the generic"
    }
    onClick={isDefault ? undefined : onClick}
  />
);

/**
 * Branded products of one generic, shown when the generic is expanded.
 */
const BrandTable = ({
  generic,
  brands,
  suppliers,
  supplierById,
  onView,
  onEdit,
  onDelete,
  onSaveSuppliers,
  onSetDefault,
}) => (
  <MDBTable small hover responsive className="mb-2 bg-white">
    <thead className="grey lighten-4">
      <tr>
        <th>Brand</th>
        <th>Suppliers</th>
        <th>Variants</th>
        <th className="text-center" style={{ width: 120 }}>
          Action
        </th>
      </tr>
    </thead>
    <tbody>
      {!brands.length && (
        <tr>
          <td colSpan={4} className="text-center text-muted small py-3">
            No branded product yet for {generic?.name || "this generic"}.
          </td>
        </tr>
      )}
      {brands.map((product) => {
        return (
          <tr key={product._id}>
            <td className="align-middle">
              <strong>{product.name}</strong>
              {product.tracksLot && (
                <MDBBadge color="info" className="ml-1">
                  Lot &amp; expiry
                </MDBBadge>
              )}
              {product.manufacturer && (
                <small className="d-block text-muted">
                  {product.manufacturer}
                </small>
              )}
            </td>
            <td className="align-middle">
              <SupplierCell
                product={product}
                suppliers={suppliers}
                supplierById={supplierById}
                onSave={onSaveSuppliers}
              />
            </td>
            <td className="align-middle small">
              {variantsOf(product).map((variant, index) => (
                <div key={variant._id || index}>
                  <DefaultStar
                    isDefault={product.isDefault && variant.isDefault}
                    onClick={() => onSetDefault(product, variant)}
                  />
                  <b>{variant.name}</b>
                  {packLabel(variant, product) && (
                    <span className="text-muted">
                      {" "}
                      · {packLabel(variant, product)}
                    </span>
                  )}
                </div>
              ))}
            </td>
            <td className="text-center align-middle py-0">
              <MDBBtnGroup>
                <MDBBtn
                  size="sm"
                  color="primary"
                  className="px-2"
                  title="View"
                  onClick={() => onView(product)}
                >
                  <MDBIcon icon="eye" />
                </MDBBtn>
                <MDBBtn
                  size="sm"
                  color="info"
                  className="px-2"
                  title="Edit"
                  onClick={() => onEdit(product)}
                >
                  <MDBIcon icon="pen" />
                </MDBBtn>
                <MDBBtn
                  size="sm"
                  color="danger"
                  className="px-2"
                  title="Delete"
                  onClick={() => onDelete(product)}
                >
                  <MDBIcon icon="trash" />
                </MDBBtn>
              </MDBBtnGroup>
            </td>
          </tr>
        );
      })}
    </tbody>
  </MDBTable>
);

/**
 * Catalogs > Products: generics of the branch, each collapsible to the
 * branded products registered under it (tagged to their suppliers).
 * Hero search = generics, header search = branded products.
 */
export default function Products() {
  const {
      token,
      activePlatform,
      maxPage = 5,
    } = useSelector(({ auth }) => auth),
    [products, setProducts] = useState([]),
    [suppliers, setSuppliers] = useState([]),
    // header: branded products
    [brandSearch, setBrandSearch] = useState(""),
    [supplierId, setSupplierId] = useState(""),
    // hero: generics
    [genericSearch, setGenericSearch] = useState(""),
    [section, setSection] = useState(""),
    [phase, setPhase] = useState(""),
    [filter, setFilter] = useState(""),
    [expanded, setExpanded] = useState({}),
    [page, setPage] = useState(1),
    [selected, setSelected] = useState(null),
    [viewing, setViewing] = useState(null),
    [isLoading, setIsLoading] = useState(false),
    { addToast } = useToasts();

  const branch = activePlatform?.branchId;

  const notify = useCallback(
    (message, appearance = "error") =>
      addToast(message, { appearance, autoDismiss: true }),
    [addToast],
  );

  // reloaded after every change since the server moves the default brand
  const load = useCallback(() => {
    if (!branch || !token) return Promise.resolve();
    setIsLoading(true);
    return Promise.all([
      axioKit.universal(`${url}/browse`, token, { branch }),
      axioKit
        .universal(SUPPLIERS_URL, token, {
          clients: branch,
          category: "supplier",
        })
        .catch(() => ({ payload: [] })),
    ])
      .then(([_products, _suppliers]) => {
        setProducts(_products?.payload || []);
        setSuppliers(
          (_suppliers?.payload || []).filter(({ deletedAt }) => !deletedAt),
        );
      })
      .catch((error) => notify(error.message))
      .finally(() => setIsLoading(false));
  }, [branch, token, notify]);

  useEffect(() => {
    load();
  }, [load]);

  const supplierById = useMemo(
    () =>
      suppliers.reduce((result, supplier) => {
        result[supplier._id] = supplier;
        return result;
      }, {}),
    [suppliers],
  );

  const brandKey = brandSearch.trim().toLowerCase();
  // a header search or supplier filter narrows the brands and opens their generics
  const isBrandFiltered = !!brandKey || !!supplierId;

  // brands per generic (after the header filters); brands without a known generic kept apart
  const { byGeneric, unlinked, totals } = useMemo(() => {
    const matches = (product) =>
      (!supplierId ||
        (supplierId === "none"
          ? !supplierIdsOf(product).length
          : supplierIdsOf(product).includes(supplierId))) &&
      (!brandKey ||
        includes(
          [
            product.name,
            product.manufacturer,
            ...supplierIdsOf(product).map((id) =>
              supplierLabel(supplierById[id]),
            ),
          ],
          brandKey,
        ));

    return products.reduce(
      (result, product) => {
        const known = !!GenericProducts.find(product.pgid);
        if (known)
          result.totals[product.pgid] = (result.totals[product.pgid] || 0) + 1;
        if (!matches(product)) return result;
        if (known)
          (result.byGeneric[product.pgid] =
            result.byGeneric[product.pgid] || []).push(product);
        else result.unlinked.push(product);
        return result;
      },
      { byGeneric: {}, unlinked: [], totals: {} },
    );
  }, [products, brandKey, supplierId, supplierById]);

  const sections = useMemo(
    () =>
      [...new Set(GenericProducts.active.map(({ section: s }) => s))].sort(),
    [],
  );

  const rows = useMemo(() => {
    const key = genericSearch.trim().toLowerCase();
    return (
      GenericProducts.collections
        // retired generics only show when they still have brands
        .filter(({ id, retired }) => !retired || totals[id])
        .filter(({ id }) => !isBrandFiltered || byGeneric[id]?.length)
        .filter(({ section: s }) => !section || s === section)
        .filter(({ phases = [] }) => !phase || phases.includes(phase))
        .filter(({ id }) =>
          !filter ? true : filter === "registered" ? totals[id] : !totals[id],
        )
        .filter(
          ({ name, abbreviation, subname, category }) =>
            !key || includes([name, abbreviation, subname, category], key),
        )
    );
  }, [
    genericSearch,
    section,
    phase,
    filter,
    totals,
    byGeneric,
    isBrandFiltered,
  ]);

  const totalPages = Math.ceil(rows.length / maxPage) || 1;
  const activePage = Math.min(page, totalPages);
  const paginated = rows.slice(
    (activePage - 1) * maxPage,
    activePage * maxPage,
  );

  const registered = Object.keys(totals).length;

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const resetPage = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const handleSubmit = async (data) => {
    setIsLoading(true);
    try {
      const { success } = data._id
        ? await axioKit.update(url, data, token)
        : await axioKit.save(url, { ...data, branch }, token);
      notify(success, "success");
      setSelected(null);
      // keep the generic of the saved brand open
      setExpanded((prev) => ({ ...prev, [data.pgid]: true }));
      await load();
    } catch (error) {
      notify(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = ({ _id, name }) =>
    Swal.fire({
      title: `Delete ${name}?`,
      text: "It can no longer be tagged, ordered or dispensed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    }).then(async ({ isConfirmed }) => {
      if (!isConfirmed) return;
      try {
        const { success } = await axioKit.destroy(url, { _id }, token);
        notify(success, "success");
        await load();
      } catch (error) {
        notify(error.message);
      }
    });

  // inline update of the suppliers of a brand; the saved brand replaces the old one
  const handleSaveSuppliers = async ({ _id }, changes) => {
    try {
      const { success, payload } = await axioKit.update(
        url,
        { _id, ...changes },
        token,
      );
      setProducts((prev) =>
        prev.map((product) =>
          product._id === _id ? { ...product, ...payload } : product,
        ),
      );
      notify(success, "success");
    } catch (error) {
      notify(error.message);
      throw error;
    }
  };

  // a variant becomes the default of its generic: its brand becomes the default
  // brand (the server unsets the others) and it becomes that brand's default variant
  const handleSetDefault = async ({ _id, variants = [] }, variant) => {
    setIsLoading(true);
    try {
      const { success } = await axioKit.update(
        url,
        {
          _id,
          isDefault: true,
          ...(variants.length && {
            variants: variants.map((item) => ({
              ...item,
              isDefault: item._id === variant._id,
            })),
          }),
        },
        token,
      );
      notify(success, "success");
      await load();
    } catch (error) {
      notify(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (product) => {
    setViewing(null);
    setSelected(product);
  };

  const tableProps = {
    suppliers,
    supplierById,
    onSaveSuppliers: handleSaveSuppliers,
    onSetDefault: handleSetDefault,
    onView: setViewing,
    onEdit: handleEdit,
    onDelete: handleDelete,
  };

  return (
    <MDBCard narrow className="pb-3">
      <MDBCardBody>
        {/* header: branded products */}
        <div
          className="d-flex flex-wrap justify-content-between align-items-center mb-3"
          style={{ gap: 8 }}
        >
          <div>
            <h5 className="mb-0">
              <MDBIcon icon="cubes" className="mr-2 text-primary" />
              Products
            </h5>
            <small className="text-muted">
              <b>{registered}</b> of {GenericProducts.active.length} generics
              with a brand · {products.length} branded product(s)
            </small>
          </div>
          <div className="d-flex flex-wrap" style={{ gap: 8 }}>
            <div className="position-relative" style={{ width: 260 }}>
              <MDBIcon
                icon="tag"
                className="position-absolute text-muted"
                style={{ top: "50%", left: 10, transform: "translateY(-50%)" }}
              />
              <input
                className="form-control form-control-sm"
                style={{ paddingLeft: 30, paddingRight: 28 }}
                placeholder="Search branded product, manufacturer..."
                value={brandSearch}
                onChange={({ target }) =>
                  resetPage(setBrandSearch)(target.value)
                }
              />
              <ClearButton
                show={!!brandSearch}
                onClick={() => resetPage(setBrandSearch)("")}
              />
            </div>
            <select
              className="browser-default custom-select custom-select-sm"
              style={{ width: 180 }}
              value={supplierId}
              onChange={({ target }) => resetPage(setSupplierId)(target.value)}
            >
              <option value="">All suppliers</option>
              <option value="none">— No supplier —</option>
              {suppliers.map((supplier) => (
                <option key={supplier._id} value={supplier._id}>
                  {supplierLabel(supplier)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* hero: generics */}
        {/* search with its filters on the right, wrapping on small screens */}
        <div className="rounded blue lighten-5 p-3 mb-3">
          <div
            className="d-flex flex-wrap align-items-center"
            style={{ gap: 8 }}
          >
            <div
              className="position-relative"
              style={{ flex: "1 1 240px", maxWidth: 340 }}
            >
              <MDBIcon
                icon="search"
                className="position-absolute text-primary"
                style={{ top: "50%", left: 10, transform: "translateY(-50%)" }}
              />
              <input
                className="form-control form-control-sm"
                style={{ paddingLeft: 30, paddingRight: 28 }}
                placeholder="Search generic (e.g. Syringe, EDTA, SYR)"
                value={genericSearch}
                onChange={({ target }) =>
                  resetPage(setGenericSearch)(target.value)
                }
              />
              <ClearButton
                show={!!genericSearch}
                onClick={() => resetPage(setGenericSearch)("")}
              />
            </div>
            <select
              className="browser-default custom-select custom-select-sm"
              style={{ width: 170 }}
              value={section}
              onChange={({ target }) => resetPage(setSection)(target.value)}
            >
              <option value="">All sections</option>
              {sections.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <div className="btn-group" role="group" aria-label="Phase filter">
              {[{ key: "", abbr: "All phases" }, ...CONSUMABLE_PHASES].map(
                ({ key, abbr, label }) => (
                  <button
                    key={key || "all"}
                    type="button"
                    title={label}
                    className={`btn btn-sm m-0 z-depth-0 ${
                      phase === key ? "btn-info" : "btn-outline-info"
                    }`}
                    style={{ textTransform: "none" }}
                    onClick={() => resetPage(setPhase)(key)}
                  >
                    {abbr}
                  </button>
                ),
              )}
            </div>
            <div className="btn-group" role="group" aria-label="Brand filter">
              {FILTERS.map(({ key, label }) => (
                <button
                  key={key || "all"}
                  type="button"
                  className={`btn btn-sm m-0 z-depth-0 ${
                    filter === key ? "btn-primary" : "btn-outline-primary"
                  }`}
                  style={{ textTransform: "none" }}
                  onClick={() => resetPage(setFilter)(key)}
                >
                  {label}
                </button>
              ))}
            </div>
            <small className="text-muted ml-auto">
              {rows.length} generic(s)
            </small>
          </div>
        </div>

        {/* branded products without a known generic */}
        {unlinked.length > 0 && (
          <div className="border border-warning rounded mb-3">
            <div
              role="button"
              tabIndex={0}
              className="px-3 py-2 d-flex align-items-center amber lighten-5"
              style={{ cursor: "pointer" }}
              onClick={() => toggle("unlinked")}
              onKeyDown={(e) => e.key === "Enter" && toggle("unlinked")}
            >
              <MDBIcon
                icon={expanded.unlinked ? "chevron-down" : "chevron-right"}
                className="mr-2 text-muted"
              />
              <MDBIcon icon="unlink" className="mr-2 text-warning" />
              <b>No generic</b>
              <small className="text-muted ml-2">
                {unlinked.length} branded product(s); edit them to choose their
                generic.
              </small>
            </div>
            {expanded.unlinked && (
              <div className="px-3 pt-2">
                <BrandTable brands={unlinked} {...tableProps} />
              </div>
            )}
          </div>
        )}

        {/* generics */}
        <div className="border rounded">
          {!paginated.length && (
            <p className="text-center text-muted my-4">
              {isLoading
                ? "Loading..."
                : isBrandFiltered
                  ? "No branded product matches the search."
                  : "No generic product found."}
            </p>
          )}
          {paginated.map((generic) => {
            const brands = byGeneric[generic.id] || [];
            const total = totals[generic.id] || 0;
            const isOpen =
              expanded[generic.id] || (isBrandFiltered && brands.length > 0);
            return (
              <div key={generic.id} className="border-bottom">
                <div
                  role="button"
                  tabIndex={0}
                  className={`px-3 py-2 d-flex align-items-center ${
                    isOpen ? "blue lighten-5" : ""
                  }`}
                  style={{ cursor: "pointer" }}
                  onClick={() => toggle(generic.id)}
                  onKeyDown={(e) => e.key === "Enter" && toggle(generic.id)}
                >
                  <MDBIcon
                    icon={isOpen ? "chevron-down" : "chevron-right"}
                    className="mr-2 text-muted"
                    style={{ width: 12 }}
                  />
                  <code className="mr-2">{generic.abbreviation}</code>
                  <div
                    className="text-truncate mr-2"
                    style={{ minWidth: 0, flex: 1 }}
                  >
                    <b>{generic.name}</b>
                    {generic.subname && (
                      <small className="text-muted"> · {generic.subname}</small>
                    )}
                    <small className="d-block text-muted">
                      {generic.section} · {generic.category} · uom:{" "}
                      {generic.uom}
                    </small>
                  </div>
                  {(generic.phases || []).map((key) => {
                    const info = CONSUMABLE_PHASES.find((p) => p.key === key);
                    return info ? (
                      <MDBBadge
                        key={key}
                        color={info.color}
                        className="mr-2"
                        title={info.label}
                      >
                        {info.abbr}
                      </MDBBadge>
                    ) : null;
                  })}
                  {generic.retired && (
                    <MDBBadge
                      color="light"
                      className="mr-2"
                      title={generic.note}
                    >
                      Retired
                    </MDBBadge>
                  )}
                  {total ? (
                    <MDBBadge
                      color="primary"
                      className="mr-2"
                      style={{ minWidth: 70 }}
                    >
                      {isBrandFiltered && brands.length !== total
                        ? `${brands.length} of ${total}`
                        : total}{" "}
                      brand{total > 1 ? "s" : ""}
                    </MDBBadge>
                  ) : (
                    <MDBBadge
                      color="light"
                      className="text-danger mr-2"
                      style={{ minWidth: 70 }}
                    >
                      no brand
                    </MDBBadge>
                  )}
                  {!generic.retired && (
                    <MDBBtn
                      size="sm"
                      color="primary"
                      className="m-0 px-2"
                      title={`Add branded product for ${generic.name}`}
                      disabled={!branch}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelected({ pgid: generic.id });
                      }}
                    >
                      <MDBIcon icon="plus" />
                    </MDBBtn>
                  )}
                </div>

                {isOpen && (
                  <div className="px-3 pt-2 pb-1 grey lighten-5">
                    <BrandTable
                      generic={generic}
                      brands={brands}
                      {...tableProps}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="d-flex justify-content-between align-items-center mt-2">
          <TableRowCount disablePageSelect={false} />
          <Pagination
            total={totalPages}
            page={activePage}
            setPage={(action) => {
              const next =
                typeof action === "number"
                  ? action
                  : activePage + (action ? 1 : -1);
              if (next >= 1 && next <= totalPages) setPage(next);
            }}
          />
        </div>
      </MDBCardBody>

      <ProductView
        product={viewing}
        toggle={() => setViewing(null)}
        suppliers={suppliers}
        onEdit={handleEdit}
      />

      <ProductForm
        show={!!selected}
        toggle={() => setSelected(null)}
        selected={selected}
        onSubmit={handleSubmit}
        suppliers={suppliers}
        isLoading={isLoading}
      />
    </MDBCard>
  );
}
